import React from 'react';
import { HashRouter as Router, Route, Switch } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useSelector } from 'react-redux';
import Login from './modules/login/Login';
import Splash from './modules/login/Splash';
import LoaderDialog from './components/LoaderDialog';
import { localRoutes } from './data/constants';
import Register from './modules/login/Register';
import VerifyEmail from './modules/login/VerifyEmail';
import PendingApproval from './modules/login/PendingApproval';
import EmploymentUpdateGate from './modules/student/EmploymentUpdateGate';
import VerifyEmailGate from './modules/student/VerifyEmailGate';
import NewStudentGate from './modules/student/NewStudentGate';

import ContentSwitch from './modules/ContentSwitch';
import EventCheckinPublic from './modules/community/EventCheckinPublic';
import EventCheckinManual from './modules/community/EventCheckinManual';
import BlueprintPublic from './modules/community/BlueprintPublic';
import { useIdleLogout } from './hooks/useIdleLogout';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { usePresenceHeartbeat } from './hooks/usePresenceHeartbeat';

const App: React.FC = () => {
  const coreState: any = useSelector((state: any) => state.core);
  const { isLoadingUser, user, globalLoader } = coreState;
  useIdleLogout(!!user);
  useTokenRefresh(!!user);
  usePresenceHeartbeat(!!user);
  if (isLoadingUser) {
    return <Splash />;
  }
  return (
    <Router>
      <ToastContainer />
      <>
        <LoaderDialog open={globalLoader} />
        <Switch>
          <Route
            path={localRoutes.eventCheckinManual}
            exact
            component={EventCheckinManual}
          />
          <Route
            path={localRoutes.eventCheckinPublic}
            component={EventCheckinPublic}
          />
          {/* A student's portfolio, viewable by an employer with no account. */}
          <Route
            path={localRoutes.blueprintPublic}
            component={BlueprintPublic}
          />
          <Route path="/">
            {user ? (
              // A self-registered student can sign in before anyone has
              // approved them — they get the holding screen instead of the
              // app. The server enforces this too (ApprovalGuard); this is
              // only so they see something that explains itself.
              user.approvalStatus === 'PENDING' ||
              user.approvalStatus === 'REJECTED' ? (
                <PendingApproval />
              ) : (
                // Three gates, outermost first. Each checks once and then
                // either takes over the screen or passes straight through,
                // so a student only ever faces one at a time.
                //   1. confirm the address, for accounts created since
                //      verification became mandatory
                //   2. the quarterly employment update
                //   3. the restricted nav until their first enrolment
                <VerifyEmailGate>
                  <EmploymentUpdateGate>
                    {/* A brand-new student with no enrollment yet sees only
                      Home + Browse Courses, not the full portal. */}
                    <NewStudentGate>
                      <ContentSwitch />
                    </NewStudentGate>
                  </EmploymentUpdateGate>
                </VerifyEmailGate>
              )
            ) : (
              <Switch>
                <Route path={localRoutes.register} component={Register} />
                <Route path={localRoutes.verifyEmail} component={VerifyEmail} />
                <Route component={Login} />
              </Switch>
            )}
          </Route>
        </Switch>
      </>
    </Router>
  );
};

export default App;
